import React, { useEffect, useState } from 'react';
import { Button, Row, Select, Typography, App } from 'antd';
import { FilterOutlined, PlusOutlined } from '@ant-design/icons';
import { QualificationFlow, QualifyingFlowQuestion, salesChannelLabel } from '../../../../dto/qualifying-flow-models';
import { SalesChannel } from '../../../../dto/model';
import { getQualificationFlows, postQualificationFlow, deleteQualificationFlow, getQualifyingQuestionsAdmin } from '../../../../services/admin-page-service';
import { GetAccessToken } from '../../../../utils/auth-utils';
import { useMsal } from '@azure/msal-react';
import { scopes } from '../../../../authConfig';
import { QualificationFlowCard } from './qualifying-flow-card';
import { QualificationFlowForm } from './qualifying-flow-form';

const { Title, Text } = Typography;

const CHANNEL_FILTER_OPTIONS = Object.values(SalesChannel)
  .filter((c) => c !== SalesChannel.MissingQueue)
  .map((c) => ({ value: c, label: salesChannelLabel[c] }));

export const QualificationFlowsComponent = () => {
  const { instance, inProgress } = useMsal();
  const { notification } = App.useApp();
  const [flows, setFlows] = useState<QualificationFlow[]>([]);
  const [allQuestions, setAllQuestions] = useState<QualifyingFlowQuestion[]>([]);
  const [channelFilter, setChannelFilter] = useState<SalesChannel | undefined>(undefined);
  const [displayAddForm, setDisplayAddForm] = useState(false);
  const [displayAddButton, setDisplayAddButton] = useState(true);
  const [newFlow, setNewFlow] = useState<QualificationFlow | null>(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    const token = await GetAccessToken(instance, inProgress, scopes.salesAppointmentApi);
    const [flowsResult, questionsResult] = await Promise.all([
      getQualificationFlows(token.accessToken),
      getQualifyingQuestionsAdmin(token.accessToken),
    ]);
    if (flowsResult.isSuccess) setFlows(flowsResult.data ?? []);
    if (questionsResult.isSuccess) setAllQuestions(questionsResult.data ?? []);
  };

  const handleSubmit = async (flow: QualificationFlow) => {
    setLoading(true);
    try {
      const token = await GetAccessToken(instance, inProgress, scopes.salesAppointmentApi);
      const result = await postQualificationFlow(token.accessToken, flow);

      if (result.isSuccess) {
        notification.success({ message: 'Flow saved successfully' });
        setDisplayAddForm(false);
        setDisplayAddButton(true);
        setNewFlow(null);
        await fetchData();
      } else {
        notification.error({
          message: 'Failed to save flow',
          description: result.errors?.join(', ') || 'Unknown error',
        });
      }
    } catch (e: any) {
      notification.error({ message: 'Failed to save flow', description: e.message });
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id: number) => {
    setLoading(true);
    try {
      const token = await GetAccessToken(instance, inProgress, scopes.salesAppointmentApi);
      const result = await deleteQualificationFlow(token.accessToken, id);

      if (result.isSuccess) {
        notification.success({ message: 'Flow deleted' });
        await fetchData();
      } else {
        notification.error({
          message: 'Failed to delete flow',
          description: result.errors?.join(', ') || 'Unknown error',
        });
      }
    } catch (e: any) {
      notification.error({ message: 'Failed to delete flow', description: e.message });
    } finally {
      setLoading(false);
    }
  };

  const handleAddNew = () => {
    const emptyFlow: QualificationFlow = {
      id: 0,
      feId: crypto.randomUUID(),
      name: '',
      description: '',
      salesChannel: SalesChannel.LeadQueue,
      isActive: false,
      questions: [],
    };
    setNewFlow(emptyFlow);
    setDisplayAddForm(true);
    setDisplayAddButton(false);
  };

  const handleCancelAdd = () => {
    setDisplayAddForm(false);
    setDisplayAddButton(true);
    setNewFlow(null);
  };

  const handleDuplicate = (flow: QualificationFlow) => {
    setNewFlow(flow);
    setDisplayAddForm(true);
    setDisplayAddButton(false);
  };

  const displayedFlows = flows.filter(
    (f) => !channelFilter || f.salesChannel === channelFilter
  );

  return (
    <div className="container flex-admin-vertical-container" style={{ width: 900, justifyContent: 'flex-start' }}>
      <Row style={{ marginBottom: '8px' }}>
        <Title level={4} style={{ marginBottom: 0 }}>
          Qualification Flows
        </Title>
        <Text type="secondary" style={{ display: 'block', marginTop: '5px' }}>
          Define qualification flows by assigning ordered questions to a sales channel. Only one flow can be active per channel at a time.
        </Text>
      </Row>

      {displayAddButton && (
        <Row justify="space-between" align="middle" style={{ marginBottom: '20px', marginTop: '20px' }}>
          <Select
            placeholder="Filter by Channel"
            allowClear
            value={channelFilter}
            onChange={setChannelFilter}
            suffixIcon={<FilterOutlined />}
            options={CHANNEL_FILTER_OPTIONS}
            style={{ width: 200 }}
          />
          <Button type="primary" icon={<PlusOutlined />} onClick={handleAddNew}>
            Add Flow
          </Button>
        </Row>
      )}

      {displayAddForm && newFlow && (
        <QualificationFlowForm
          flow={newFlow}
          allQuestions={allQuestions}
          onSubmit={handleSubmit}
          onCancel={handleCancelAdd}
          loading={loading}
        />
      )}

      {!displayAddForm &&
        displayedFlows.map((flow) => (
          <QualificationFlowCard
            key={flow.feId}
            flow={flow}
            allQuestions={allQuestions}
            onSubmit={handleSubmit}
            onDelete={handleDelete}
            onDuplicate={handleDuplicate}
            shouldDisplayAddButton={setDisplayAddButton}
            loading={loading}
          />
        ))}
    </div>
  );
};
